import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import { Alert, Box, Chip, IconButton, Pagination, Tooltip, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PublicIcon from '@mui/icons-material/Public';
import RefreshIcon from '@mui/icons-material/Refresh';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@tenant/utility/constants/default';
import useGetAuditLog from 'redux/slices/auditLog/hooks/useGetAuditLog';
import { mapAuditRowsFromResponse, computeLoadEarlierWindow } from 'redux/slices/auditLog/helpers';
import AuditLogFilters, { AUDIT_LOG_EMPTY_FILTER } from './AuditLogFilters';
import WindowClampedBanner from './WindowClampedBanner';

// AC-6 (Occurred At descending, from GET /api/audit-log): the store already returns
// OccurredAt-descending order (AuditQueryService.cs:61) — no client-side re-sort here.
const formatOccurredAt = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().replace('T', ' ').slice(0, 19);
};

const AuditLogTable = () => {
  const { filter } = useSelector((s) => s.auditLog);
  const [pageIndex, setPageIndex] = useState(filter?.pageIndex ?? DEFAULT_PAGE_INDEX);
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE;
  const [appliedFilter, setAppliedFilter] = useState({ ...AUDIT_LOG_EMPTY_FILTER, ...filter });

  const { isLoading, isRefetching, isError, refetch } = useGetAuditLog({
    pageIndex,
    pageSize,
    filterState: appliedFilter,
  });

  const { list } = useSelector((s) => s.auditLog);
  const totalCount = list?.TotalCount ?? 0;
  const windowClamped = list?.WindowClamped === true;
  const items = list?.Items ?? [];
  const rows = useMemo(() => mapAuditRowsFromResponse(items), [items]);

  const handleApplyFilter = (draftFilter) => {
    setAppliedFilter(draftFilter);
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  const handleResetFilter = () => {
    setAppliedFilter(AUDIT_LOG_EMPTY_FILTER);
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  const handleLoadEarlier = () => {
    const oldestShownOccurredAt = rows.length ? rows[rows.length - 1].OccurredAt : undefined;
    const { occurredFrom, occurredTo } = computeLoadEarlierWindow(
      appliedFilter.occurredFrom,
      appliedFilter.occurredTo,
      oldestShownOccurredAt,
    );
    setAppliedFilter((prev) => ({ ...prev, occurredFrom, occurredTo }));
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  const refreshTableAction = () => {
    if (pageIndex === DEFAULT_PAGE_INDEX) {
      refetch();
      return;
    }
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  const columns = [
    {
      field: 'OccurredAt',
      headerName: 'Occurred At (UTC)',
      width: 190,
      sortable: false,
      renderCell: (params) => formatOccurredAt(params.value),
    },
    {
      field: 'ActorLabel',
      headerName: 'Actor',
      width: 190,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size='small'
          variant={params.row.IsSystemActor ? 'filled' : 'outlined'}
          color={params.row.IsSystemActor ? 'default' : 'primary'}
          icon={
            params.row.IsSystemActor ? (
              <SettingsIcon fontSize='small' />
            ) : (
              <PersonOutlineIcon fontSize='small' />
            )
          }
          label={params.value}
        />
      ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'EntityType',
      headerName: 'Entity',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Typography variant='body2'>{params.value}</Typography>
          <Typography variant='caption' color='text.secondary'>
            {params.row.EntityId}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'AppId',
      headerName: 'App',
      width: 170,
      sortable: false,
      renderCell: (params) =>
        params.row.IsPlatformGlobal ? (
          <Chip
            size='small'
            variant='outlined'
            icon={<PublicIcon fontSize='small' />}
            label='Platform (global)'
          />
        ) : (
          `#${params.value}`
        ),
    },
    {
      field: 'CorrelationId',
      headerName: 'Correlation ID',
      width: 170,
      sortable: false,
      renderCell: (params) => params.value || '—',
    },
  ];

  return (
    <>
      {/* The filter panel is deliberately NOT wrapped in AppsHeader. AppsHeader is a hard
          `height: 60`, `alignItems: center` band (AppsContainer/AppsHeader.js) built for the
          single row of controls every sibling list puts there (PipelineTable: search + CTA).
          This panel is a wrapping multi-row grid of eight controls, so nesting it in that band
          overflowed it symmetrically: the first filter row was pushed ABOVE the band and clipped
          by the `overflow: hidden` on the AppsContainer root, while the bottom of the panel
          overlapped the banner and table below — those are laid out as if the header were still
          only 60px tall. The panel carries the band's own bottom divider instead, so it sizes to
          its content. The band's `h6` title went with it: AppsContainer already renders the
          `Developer / Audit Log` breadcrumb, so it was a duplicate. */}
      <Box sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
        <AuditLogFilters onApply={handleApplyFilter} onReset={handleResetFilter} />
      </Box>

      {windowClamped && <WindowClampedBanner onLoadEarlier={handleLoadEarlier} />}

      <Box sx={{ mt: 2 }}>
        {isError ? (
          <Alert severity='error'>
            Something went wrong loading the audit log. Please try again.
          </Alert>
        ) : !isLoading && rows.length === 0 ? (
          <Alert severity='info'>No audit records match your filters.</Alert>
        ) : (
          // A DataGrid needs a parent of definite width or it sizes to content and overflows the
          // page (clipping the last column) instead of scrolling internally — mirrors the width:100%
          // wrapper the sibling list tables (PipelineTable / AppInstanceTable) use.
          <Box sx={{ width: '100%' }}>
            <DataGrid
              hideFooter
              autoHeight
              disableColumnSelector
              disableColumnMenu
              rows={rows}
              columns={columns}
              getRowId={(row) => row.Id}
              loading={isLoading || isRefetching}
            />
          </Box>
        )}
      </Box>

      <FooterWrapper className='footer'>
        <Box justifyContent='center' className='footerContainer'>
          <Pagination
            count={Math.ceil(totalCount / pageSize) || 1}
            page={pageIndex}
            onChange={(e, v) => setPageIndex(v)}
            showFirstButton
            showLastButton
          />
          <Typography>Total: {totalCount}</Typography>
          <Tooltip title='Refresh'>
            <IconButton
              disabled={isRefetching}
              size='small'
              sx={{ ml: 2 }}
              onClick={refreshTableAction}
            >
              <RefreshIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
      </FooterWrapper>
    </>
  );
};

export default AuditLogTable;
